/**
 * System Admin — External Audits
 *
 * Single source of truth for every external audit period across the platform.
 *
 * Tabs:
 *   • Awaiting auditor — locked, no auditor_id yet (System Admin must assign)
 *   • In progress     — open or locked WITH an auditor assigned
 *   • Closed          — certificate issued, pages permanently locked
 *   • Cancelled       — admin or company cancelled before certification
 *   • All             — everything
 *
 * Per-row actions are state-aware:
 *   • Awaiting → Assign Auditor
 *   • In progress → Revoke Auditor, Cancel Period (if no cert)
 *   • Closed → View Certificate
 *   • Cancelled → (read-only)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, RefreshCw, FileText, Clock, Building2, User, X,
  CheckCircle, Lock as LockIcon, XCircle, Award,
} from 'lucide-react';
import { notify } from '@/core/notifications/notify';
import PageInfoButton from '@/components/PageInfoButton';
import { useModal, ConfirmModal } from '@components/modal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface AuditPeriodRow {
  id: number;
  status: 'open' | 'locked' | 'closed' | 'cancelled';
  opened_at: string;
  locked_at: string | null;
  closed_at: string | null;
  opened_by: number | null;
  auditor_id: number | null;
  framework_assignment_id: number;
  scope_notes: string | null;
  cancellation_reason: string | null;
  company_id: number;
  company_name: string;
  framework_id: number;
  framework_name: string;
  framework_version: string | null;
  opened_by_first_name: string | null;
  opened_by_last_name: string | null;
  opened_by_email: string | null;
  auditor_first_name: string | null;
  auditor_last_name: string | null;
  auditor_email: string | null;
  pages_locked: number;
  certificate_snapshot_id: number | null;
  certification_id: number | null;
  certification_status: string | null;
  certification_expiry_date: string | null;
}

interface ExternalAuditor {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

type Tab = 'awaiting' | 'in_progress' | 'closed' | 'cancelled' | 'all';

const TAB_LABEL: Record<Tab, string> = {
  awaiting: 'Awaiting auditor',
  in_progress: 'In progress',
  closed: 'Closed',
  cancelled: 'Cancelled',
  all: 'All',
};

const STATUS_PILL: Record<string, string> = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  locked: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-300',
};

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const fullName = (first: string | null, last: string | null) =>
  [first, last].filter(Boolean).join(' ').trim();

const AdminAuditPeriodsInbox: React.FC = () => {
  const { openModal } = useModal();
  const [tab, setTab] = useState<Tab>('awaiting');
  const [periods, setPeriods] = useState<AuditPeriodRow[]>([]);
  const [auditors, setAuditors] = useState<ExternalAuditor[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [assignFor, setAssignFor] = useState<AuditPeriodRow | null>(null);
  const [selectedAuditor, setSelectedAuditor] = useState<number | ''>('');
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`/api/compliance/audit-periods/admin/list?status=all`, { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/compliance/audit-periods/admin/external-auditors', { headers: authHeaders() }).then(r => r.json()),
      ]);
      if (r1?.success) setPeriods(r1.data ?? []);
      else notify.error(r1?.message ?? 'Failed to load audit periods');
      if (r2?.success) setAuditors(r2.data ?? []);
    } catch (e: any) {
      notify.error(e?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { awaiting: 0, in_progress: 0, closed: 0, cancelled: 0, all: periods.length };
    for (const p of periods) {
      if (p.status === 'closed') c.closed++;
      else if (p.status === 'cancelled') c.cancelled++;
      else if (!p.auditor_id) c.awaiting++;
      else c.in_progress++;
    }
    return c;
  }, [periods]);

  const visible = useMemo(() => {
    return periods.filter(p => {
      if (tab === 'all') return true;
      if (tab === 'awaiting') return (p.status === 'open' || p.status === 'locked') && !p.auditor_id;
      if (tab === 'in_progress') return (p.status === 'open' || p.status === 'locked') && !!p.auditor_id;
      if (tab === 'closed') return p.status === 'closed';
      if (tab === 'cancelled') return p.status === 'cancelled';
      return true;
    });
  }, [periods, tab]);

  const submitAssign = async () => {
    if (!assignFor || !selectedAuditor) return;
    setBusyId(assignFor.id);
    try {
      const res = await fetch(`/api/compliance/audit-periods/${assignFor.id}/assign-auditor`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ user_id: selectedAuditor, reason: reason || undefined }),
      });
      const j = await res.json();
      if (!res.ok || !j?.success) {
        notify.error(j?.message ?? 'Assignment failed');
        return;
      }
      notify.success('External auditor assigned. They will see this company on their audit dashboard.');
      setAssignFor(null);
      setSelectedAuditor('');
      setReason('');
      await load();
    } catch (e: any) {
      notify.error(e?.message ?? 'Assignment failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = (p: AuditPeriodRow) => {
    if (!p.auditor_id) return;
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Revoke External Auditor',
        message: `Remove ${fullName(p.auditor_first_name, p.auditor_last_name) || p.auditor_email} as the external auditor for "${p.company_name} — ${p.framework_name}"?\n\nThe audit period stays open and the pages stay locked. You'll need to assign a different external auditor to continue.`,
        confirmText: 'Revoke Auditor',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          setBusyId(p.id);
          try {
            const res = await fetch(`/api/compliance/audit-periods/${p.id}/revoke-auditor`, { method: 'POST', headers: authHeaders() });
            const j = await res.json();
            if (!res.ok || !j?.success) { notify.error(j?.message ?? 'Revoke failed'); return; }
            notify.success('Auditor revoked.');
            await load();
          } catch (e: any) {
            notify.error(e?.message ?? 'Revoke failed');
          } finally {
            setBusyId(null);
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleAdminCancel = (p: AuditPeriodRow) => {
    if (p.certificate_snapshot_id) {
      notify.error('A certificate has been issued for this period — it cannot be cancelled.');
      return;
    }
    const reasonInput = window.prompt(`Cancel audit period for "${p.company_name} — ${p.framework_name}".\n\nThis will UNLOCK all ${p.pages_locked} pages and the audit period will be marked cancelled.\n\nReason for cancellation (required, will be recorded):`, '');
    if (!reasonInput || !reasonInput.trim()) return;
    setBusyId(p.id);
    fetch(`/api/compliance/audit-periods/${p.id}/admin-cancel`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ reason: reasonInput.trim() }),
    })
      .then(r => r.json())
      .then(j => {
        if (!j?.success) { notify.error(j?.message ?? 'Cancel failed'); return; }
        notify.success('Audit period cancelled. Pages unlocked.');
        load();
      })
      .catch(e => notify.error(e?.message ?? 'Cancel failed'))
      .finally(() => setBusyId(null));
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-1">
            <h1 className="text-3xl font-bold text-slate-900">External Audits</h1>
            <PageInfoButton />
          </div>
          <p className="text-slate-500 text-sm">
            Every external audit period across the platform — assign auditors, monitor progress, and view certifications.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 mb-4 w-fit">
        {(['awaiting', 'in_progress', 'closed', 'cancelled', 'all'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
              tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {TAB_LABEL[t]} ({counts[t]})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <RefreshCw className="w-6 h-6 animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <ShieldCheck className="w-10 h-10 text-slate-300 mb-3 inline-block" />
          <h3 className="font-semibold text-slate-700">Nothing in this tab</h3>
          <p className="text-sm text-slate-400 mt-1">Try a different filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(p => {
            const auditorName = fullName(p.auditor_first_name, p.auditor_last_name);
            const openedByName = fullName(p.opened_by_first_name, p.opened_by_last_name);
            const isAwaiting = (p.status === 'open' || p.status === 'locked') && !p.auditor_id;
            const isInProgress = (p.status === 'open' || p.status === 'locked') && !!p.auditor_id;
            const hasCert = !!p.certificate_snapshot_id || !!p.certification_id;
            return (
              <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Header line */}
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1 flex-wrap">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      <span className="font-semibold text-slate-700">{p.company_name}</span>
                      <span>·</span>
                      <span>{p.framework_name}{p.framework_version ? ` v${p.framework_version}` : ''}</span>
                      <span>·</span>
                      <span className="text-slate-400">Period #{p.id}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_PILL[p.status]}`}>
                        {p.status}
                      </span>
                      {isAwaiting && (
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                          awaiting auditor
                        </span>
                      )}
                      {hasCert && (
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200 flex items-center gap-1">
                          <Award className="w-3 h-3" /> certified
                        </span>
                      )}
                    </div>

                    {/* Detail grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 mt-3 text-[11px]">
                      <div>
                        <div className="text-slate-400 uppercase font-bold tracking-wide text-[10px]">Pages locked</div>
                        <div className="text-slate-700 font-medium flex items-center gap-1 mt-0.5"><FileText className="w-3 h-3" /> {p.pages_locked}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 uppercase font-bold tracking-wide text-[10px]">Opened</div>
                        <div className="text-slate-700 font-medium flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {fmtDate(p.opened_at)}</div>
                        {openedByName && <div className="text-slate-500 mt-0.5">by {openedByName}</div>}
                      </div>
                      <div>
                        <div className="text-slate-400 uppercase font-bold tracking-wide text-[10px]">External auditor</div>
                        {p.auditor_id ? (
                          <div className="text-slate-700 font-medium flex items-center gap-1 mt-0.5">
                            <User className="w-3 h-3" /> {auditorName || p.auditor_email}
                          </div>
                        ) : (
                          <div className="text-amber-700 font-semibold mt-0.5">Not assigned</div>
                        )}
                        {p.auditor_email && <div className="text-slate-500 mt-0.5">{p.auditor_email}</div>}
                      </div>
                      <div>
                        <div className="text-slate-400 uppercase font-bold tracking-wide text-[10px]">
                          {p.status === 'closed' ? 'Closed' : p.status === 'cancelled' ? 'Cancelled' : 'Locked at'}
                        </div>
                        <div className="text-slate-700 font-medium flex items-center gap-1 mt-0.5">
                          <LockIcon className="w-3 h-3" /> {fmtDate(p.closed_at ?? p.locked_at)}
                        </div>
                      </div>
                    </div>

                    {/* Scope / cancellation reason */}
                    {p.scope_notes && (
                      <div className="mt-3 text-xs text-slate-600 italic bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                        <span className="font-semibold text-slate-700 not-italic">Scope:</span> {p.scope_notes}
                      </div>
                    )}
                    {p.cancellation_reason && (
                      <div className="mt-3 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        <span className="font-semibold text-amber-700">Cancellation reason:</span> {p.cancellation_reason}
                      </div>
                    )}

                    {/* Certificate strip */}
                    {hasCert && (
                      <div className="mt-3 text-xs bg-violet-50 border border-violet-200 rounded-md px-3 py-2 flex items-center gap-3 flex-wrap">
                        <Award className="w-3.5 h-3.5 text-violet-600 flex-shrink-0" />
                        <span className="font-semibold text-violet-800">Certificate issued</span>
                        {p.certification_status && <span className="text-violet-700 capitalize">· {p.certification_status}</span>}
                        {p.certification_expiry_date && <span className="text-violet-700">· expires {fmtDate(p.certification_expiry_date)}</span>}
                        {p.certification_id && (
                          <Link to={`/admin/certifications/${p.certification_id}`} className="ml-auto text-violet-700 underline hover:text-violet-900">
                            View certificate
                          </Link>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {isAwaiting && (
                      <button
                        onClick={() => { setAssignFor(p); setSelectedAuditor(''); setReason(''); }}
                        disabled={busyId === p.id}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <ShieldCheck className="w-4 h-4" /> Assign Auditor
                      </button>
                    )}
                    {isInProgress && (
                      <>
                        <button
                          onClick={() => handleRevoke(p)}
                          disabled={busyId === p.id}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                        >
                          <X className="w-3.5 h-3.5" /> Revoke Auditor
                        </button>
                        {!hasCert && (
                          <button
                            onClick={() => handleAdminCancel(p)}
                            disabled={busyId === p.id}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                          >
                            <XCircle className="w-3.5 h-3.5" /> Cancel Period
                          </button>
                        )}
                      </>
                    )}
                    {p.status === 'closed' && (
                      <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg">
                        <CheckCircle className="w-3.5 h-3.5" /> Closed
                      </span>
                    )}
                    {p.status === 'cancelled' && (
                      <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 rounded-lg">
                        <XCircle className="w-3.5 h-3.5" /> Cancelled
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign modal */}
      {assignFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Assign External Auditor
              </h2>
              <button onClick={() => setAssignFor(null)} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-700 space-y-0.5">
                <div><span className="font-semibold">Company:</span> {assignFor.company_name}</div>
                <div><span className="font-semibold">Framework:</span> {assignFor.framework_name}{assignFor.framework_version ? ` v${assignFor.framework_version}` : ''}</div>
                <div><span className="font-semibold">Period:</span> #{assignFor.id} · {assignFor.pages_locked} page(s) locked</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">External Auditor</label>
                <select
                  value={selectedAuditor}
                  onChange={e => setSelectedAuditor(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose an external auditor…</option>
                  {auditors.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.first_name} {a.last_name} ({a.email})
                    </option>
                  ))}
                </select>
                {auditors.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No external auditors found. Create a user with the "External Auditor" permission profile first.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason / scope <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  placeholder={`External audit for ${assignFor.framework_name}`}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
              <button
                onClick={() => setAssignFor(null)}
                className="px-4 py-2.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-white font-medium"
              >
                Cancel
              </button>
              <button
                onClick={submitAssign}
                disabled={busyId === assignFor.id || !selectedAuditor}
                className="px-4 py-2.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                {busyId === assignFor.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditPeriodsInbox;
